import type { MessageCatalog } from '@mt/protocol';

/** Сообщения сервера (коды — packages/protocol/src/messages.ts). */
export const srv: MessageCatalog = {
  // ── соединение и сессия ──
  'err.rateLimited': 'too many requests, please wait',
  'err.badMessage': 'invalid message',
  'err.internal': 'internal server error',
  'err.adminNoPlay': 'administrators do not take part in games',
  'err.authRequired': 'sign-in required',
  'err.adminRequired': 'administrator sign-in required',
  'err.adminOnly': 'available to administrators only',
  'err.sessionExpired': 'session not found or expired — please sign in again',
  'err.badData': 'invalid data',
  'err.tooManyConnections': 'too many connections',
  // ── столы ──
  'err.roomNotFound': 'table not found',
  'err.unknownCategory': 'unknown table category',
  'err.lowBalanceTable': 'not enough credits for this table’s ante',
  'err.lowBalance': 'not enough credits for the ante',
  'err.inOtherGame': 'you are playing a game at another table',
  'err.notSeated': 'you are not seated at the table',
  'err.notInGame': 'you are not in the current game',
  'err.notParticipant': 'you are not a participant in this game',
  'err.gameFinished': 'the game is over',
  'room.notStarted': 'Game not started: {detail}',
  // ── учётная запись ──
  'err.banned': 'account banned',
  'err.bannedReason': 'account banned: {reason}',
  'err.accountNotFound': 'account not found',
  'err.userNotFound': 'user not found',
  'err.playerNotFound': 'player not found',
  'err.badReason': 'invalid reason',
  'err.noBanBots': 'bots cannot be banned',
  'err.botBalanceAuto': 'bot balances are topped up automatically',
  'err.botTopUpOnly': 'top-ups are available for bots only',
  'err.resetDuringGame': 'balance reset is unavailable during a game — wait for the settlement',
  'err.nameShort': 'the name must be at least 2 characters long',
  'err.nameChars': 'names may contain letters, digits, spaces, dots, hyphens and underscores',
  'err.nameBot': 'the name must not impersonate a bot',
  'err.nameTaken': 'this name is already taken — please choose another',
  // ── вход и регистрация ──
  'err.loginFormat': 'username: 3–32 characters — Latin letters, digits, dot, hyphen, underscore; must start with a letter or digit',
  'err.loginTaken': 'this username is taken',
  'err.registerRateLimited': 'too many sign-ups from this address, please try again later',
  'err.loginRateLimited': 'too many sign-in attempts — please wait a few minutes',
  'err.loginInvalid': 'wrong username or password',
  'err.registerFields': 'enter a username, password and name',
  'err.loginFields': 'enter your username and password',
  'err.passwordMissing': 'enter a new password',
  'err.passwordCurrentWrong': 'the current password is incorrect',
  'err.passwordShort': 'the password must be at least 8 characters long',
  'err.passwordLong': 'the password is too long (128 characters at most)',
  'err.passwordLetters': 'the password must contain letters and digits',
  'err.sessionNotFound': 'session not found — please sign in again',
  'err.providerLinkedOther': 'this {provider} account is already linked to another player',
  'err.providerOtherLinked': 'a different {provider} account is already linked to your profile',
  'err.providerNotLinked': 'this provider is not linked',
  'err.providerLastMethod': 'you cannot unlink your only sign-in method — set a username and password first',
  'err.oauthNotConfigured': 'sign-in with this provider is not configured',
  'err.oauthTooMany': 'too many unfinished sign-ins, please try again later',
  'err.oauthState': 'the sign-in request is outdated or forged — please start again',
  'err.oauthProfile': 'the provider returned {status} when requesting the profile',
  'err.oauthNoToken': 'the provider did not issue a token',
  'err.oauthNoTokenDetail': 'the provider did not issue a token: {detail}',
  'err.oauthNoSubject': 'the provider did not return a user ID',
  'err.oauthCancelled': 'sign-in cancelled',
  'err.oauthProviderError': 'the provider reported an error: {detail}',
  'err.oauthIncomplete': 'incomplete response from the provider',
  'err.oauthFailed': 'could not sign in with the provider — please try again later',
  // ── история ──
  'err.gameNotFound': 'game not found',
  'err.replayParticipantsOnly': 'the replay is available to game participants only',
  'err.packageNotFound': 'no verification package found for this game',
  'err.packageParticipantsOnly': 'the package is available to game participants only',
  // ── ходы и прогноз ──
  'err.forecastLocked': 'the forecast is already locked',
  'err.betsLocked': 'bets are already locked',
  'err.folded': 'you have folded',
  'err.directionRound3': 'the direction cannot be changed in round 3 — only the number of points',
  'err.directionAllIn': 'the direction cannot be cleared after going all-in',
  'err.pointsInvalid': 'points must be strictly positive and a multiple of the price tick',
  'err.pointsInvalidTick': 'points must be strictly positive and a multiple of {tick}',
  'err.pointsAllIn': 'the points forecast cannot be cleared after going all-in',
  'err.autoLimit': 'set an auto-call limit',
  'err.noBetting': 'no betting round right now',
  'err.notYourTurn': 'it is not your turn',
  'err.directionFirst': 'choose a direction first: up or down',
  'err.allInPoints': 'set a preliminary number of points before going all-in',
  'err.checkToCall': 'cannot check: there is a bet to call',
  'err.callNothing': 'nothing to call — check instead',
  'err.betExists': 'a bet has already been made — call or raise',
  'err.betMin': 'minimum bet is {amount|cr}',
  'err.creditsLow': 'not enough credits',
  'err.raiseNoBet': 'no bet to raise — use “bet”',
  'err.raiseLimit': 'the raise limit for this round has been reached',
  'err.raiseMin': 'raise to at least {amount|cr}',
  'err.creditsNone': 'no credits available',
  'err.allInRaiseLimit': 'raise limit reached — only a call is possible',
  'err.actionUnknown': 'unknown action',
  // ── причины отмены и завершения ──
  'reason.noAntes': 'no antes',
  'reason.allOut': 'all participants are out',
  'reason.walkover': 'all other participants folded',
  'reason.walkoverMulti': 'internal error: walkover with several participants',
  'reason.flat': 'P₁ = P₀: neither “up” nor “down” happened',
  'reason.noneCorrect': 'none of the remaining players guessed the direction',
  'reason.noResult': 'no reliable result: {detail}',
  'reason.sourceDown': 'no reliable result: data source unavailable',
  'reason.serverStop': 'server shutdown',
  // ── хронология розыгрыша ──
  'log.created': 'Game created. Commitment: {commitment}…',
  'log.antes': 'Antes of {ante|cr} each paid into the pot. P₀ = {p0}.',
  'log.round1': 'Round 1: index chart and choice of direction. Price scale hidden.',
  'log.round2': 'Round 2: price scale and point distances revealed.',
  'log.round3': 'Round 3: index composition revealed — {composition}. Enter the number of points.',
  'log.locked': 'Forecasts and bets locked. The target minute candle is forming.',
  'log.uncalledReturn': '{name}: uncalled bet of {amount|cr} returned',
  'log.autoFoldNoForecast': '{name}: auto-fold — no complete forecast by lock',
  'log.autoFoldTimeout': '{name}: auto-fold — turn timed out',
  'log.roundClosed': 'Round {round}: betting closed',
  'log.settled': 'Settlement complete.',
  'log.cancelled': 'Game cancelled: {reason}. Antes returned, no commission taken.',
};
